import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Polygon, FeatureGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MapPinned, Plus, Trash2, Edit, Save, X, Radio } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

// Componente de desenho simplificado (clique para adicionar pontos)
function PolygonDrawer({ pontos, setPontos, ativo }) {
    const map = useMap();
    useEffect(() => {
        if (!ativo) return;
        const handler = (e) => {
            setPontos(prev => [...prev, [e.latlng.lat, e.latlng.lng]]);
        };
        map.on('click', handler);
        return () => map.off('click', handler);
    }, [map, ativo, setPontos]);
    return pontos.length > 0 ? (
        <Polygon positions={pontos} pathOptions={{ color: '#fbbf24', fillOpacity: 0.2, weight: 2 }} />
    ) : null;
}

export default function GeofencesPage() {
    const [geofences, setGeofences] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [criando, setCriando] = useState(false);
    const [editando, setEditando] = useState(null);
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [cor, setCor] = useState('#06b6d4');
    const [pontos, setPontos] = useState([]);
    const [mmsiEdit, setMmsiEdit] = useState({});

    const fetchAll = useCallback(async () => {
        try {
            const [gf, emp, bal] = await Promise.all([
                axios.get(`${API}/geofences`),
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`),
            ]);
            setGeofences(gf.data || []);
            setEmpurradores(emp.data || []);
            setBalsas(bal.data || []);
        } catch {
            toast.error('Erro ao carregar dados');
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const abrirCriacao = () => {
        setEditando(null);
        setNome(''); setDescricao(''); setCor('#06b6d4'); setPontos([]);
        setCriando(true);
    };

    const abrirEdicao = (gf) => {
        setEditando(gf);
        setNome(gf.nome); setDescricao(gf.descricao || ''); setCor(gf.cor || '#06b6d4');
        setPontos(gf.poligono || []);
        setCriando(true);
    };

    const salvar = async () => {
        if (!nome.trim()) return toast.error('Nome obrigatorio');
        if (pontos.length < 3) return toast.error('Clique no mapa para adicionar ao menos 3 pontos');
        try {
            const payload = { nome, descricao, cor, poligono: pontos, ativa: true };
            if (editando) {
                await axios.put(`${API}/geofences/${editando.id}`, payload);
                toast.success('Geofence atualizada');
            } else {
                await axios.post(`${API}/geofences`, payload);
                toast.success('Geofence criada');
            }
            setCriando(false);
            fetchAll();
        } catch (e) {
            toast.error('Erro: ' + (e?.response?.data?.detail || 'desconhecido'));
        }
    };

    const remover = async (id) => {
        if (!window.confirm('Remover esta geofence?')) return;
        try {
            await axios.delete(`${API}/geofences/${id}`);
            toast.success('Removida');
            fetchAll();
        } catch {
            toast.error('Erro ao remover');
        }
    };

    const salvarMmsi = async (emb, tipo) => {
        const mmsi = (mmsiEdit[emb.id] ?? emb.mmsi ?? '').trim();
        try {
            const url = tipo === 'empurrador' ? `${API}/empurradores/${emb.id}/mmsi` : `${API}/balsas/${emb.id}/mmsi`;
            await axios.put(url, { mmsi });
            toast.success(`MMSI atualizado: ${emb.nome}`);
            setMmsiEdit(prev => { const n = { ...prev }; delete n[emb.id]; return n; });
            fetchAll();
            // Reconectar worker para pegar novo MMSI
            await axios.post(`${API}/ais/reconectar`);
        } catch (e) {
            toast.error('Erro: ' + (e?.response?.data?.detail || 'desconhecido'));
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="geofences-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-heading text-white flex items-center gap-2">
                        <MapPinned className="w-7 h-7 text-cyan-400" />
                        Geofences & MMSI
                    </h1>
                    <p className="text-sm text-slate-400 font-mono mt-1">
                        Desenhe areas de interesse no mapa e cadastre o MMSI das embarcacoes
                    </p>
                </div>
                <Button onClick={abrirCriacao} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-nova-geofence">
                    <Plus className="w-4 h-4 mr-2" /> Nova Geofence
                </Button>
            </div>

            {/* Lista de geofences */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                    <CardTitle className="text-white font-heading text-base">Geofences Cadastradas ({geofences.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    {geofences.length === 0 ? (
                        <p className="text-slate-500 italic text-center py-6">Nenhuma geofence. Clique em &quot;Nova Geofence&quot;.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400">Nome</TableHead>
                                    <TableHead className="text-slate-400">Descricao</TableHead>
                                    <TableHead className="text-slate-400 text-center">Pontos</TableHead>
                                    <TableHead className="text-slate-400">Status</TableHead>
                                    <TableHead className="text-slate-400 text-right">Acoes</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {geofences.map(gf => (
                                    <TableRow key={gf.id} className="border-slate-800 hover:bg-slate-800/40">
                                        <TableCell className="text-white">
                                            <span className="inline-flex items-center gap-2">
                                                <span className="w-3 h-3 rounded-sm" style={{ background: gf.cor }} />
                                                {gf.nome}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-xs">{gf.descricao || '-'}</TableCell>
                                        <TableCell className="text-slate-400 text-center font-mono">{(gf.poligono || []).length}</TableCell>
                                        <TableCell>
                                            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm border ${
                                                gf.ativa ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700' : 'bg-slate-800 text-slate-400 border-slate-700'
                                            }`}>{gf.ativa ? 'Ativa' : 'Inativa'}</span>
                                        </TableCell>
                                        <TableCell className="text-right space-x-1">
                                            <Button size="sm" variant="ghost" onClick={() => abrirEdicao(gf)} data-testid={`btn-editar-gf-${gf.id}`}>
                                                <Edit className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => remover(gf.id)} data-testid={`btn-remover-gf-${gf.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Cadastro MMSI */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                    <CardTitle className="text-white font-heading text-base flex items-center gap-2">
                        <Radio className="w-4 h-4 text-amber-400" />
                        Cadastro de MMSI (Identificador AIS)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-xs text-slate-500 mb-3">
                        Cadastre o MMSI (7-9 digitos) de cada embarcacao. Apos salvar, o worker AIS reconecta filtrando os MMSIs cadastrados.
                    </p>
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-800 hover:bg-transparent">
                                <TableHead className="text-slate-400">Embarcacao</TableHead>
                                <TableHead className="text-slate-400">Tipo</TableHead>
                                <TableHead className="text-slate-400">MMSI</TableHead>
                                <TableHead className="text-slate-400 text-right">Acao</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {[...empurradores.map(e => ({ ...e, _t: 'empurrador' })), ...balsas.map(b => ({ ...b, _t: 'balsa' }))].map(emb => {
                                const val = mmsiEdit[emb.id] ?? emb.mmsi ?? '';
                                const dirty = mmsiEdit[emb.id] !== undefined && mmsiEdit[emb.id] !== emb.mmsi;
                                return (
                                    <TableRow key={emb.id} className="border-slate-800 hover:bg-slate-800/40">
                                        <TableCell className="text-white">{emb.nome}</TableCell>
                                        <TableCell className="text-slate-400 capitalize">{emb._t}</TableCell>
                                        <TableCell>
                                            <Input
                                                value={val}
                                                onChange={(e) => setMmsiEdit(prev => ({ ...prev, [emb.id]: e.target.value }))}
                                                placeholder="710000000"
                                                className="bg-slate-800 border-slate-700 text-white h-8 w-36 font-mono"
                                                maxLength={9}
                                                data-testid={`input-mmsi-${emb.id}`}
                                            />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                disabled={!dirty}
                                                onClick={() => salvarMmsi(emb, emb._t)}
                                                className={dirty ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-slate-800'}
                                                data-testid={`btn-salvar-mmsi-${emb.id}`}
                                            >
                                                <Save className="w-3.5 h-3.5 mr-1" /> Salvar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Dialog Criar/Editar Geofence */}
            <Dialog open={criando} onOpenChange={setCriando}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {editando ? 'Editar' : 'Nova'} Geofence
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                placeholder="Nome (ex: Porto de Vitoria do Xingu)"
                                value={nome}
                                onChange={e => setNome(e.target.value)}
                                className="bg-slate-800 border-slate-700 text-white"
                                data-testid="input-nome-geofence"
                            />
                            <Input
                                placeholder="Descricao"
                                value={descricao}
                                onChange={e => setDescricao(e.target.value)}
                                className="bg-slate-800 border-slate-700 text-white"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="text-xs text-slate-400 font-mono uppercase">Cor:</label>
                            <input
                                type="color"
                                value={cor}
                                onChange={e => setCor(e.target.value)}
                                className="h-8 w-16 bg-slate-800 border border-slate-700 rounded-sm cursor-pointer"
                            />
                            <span className="text-xs text-slate-500">
                                Clique no mapa para adicionar pontos. {pontos.length} pontos.
                            </span>
                            <Button size="sm" variant="outline" onClick={() => setPontos([])} disabled={pontos.length === 0} className="ml-auto">
                                <X className="w-3.5 h-3.5 mr-1" /> Limpar pontos
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setPontos(p => p.slice(0, -1))} disabled={pontos.length === 0}>
                                Desfazer ultimo
                            </Button>
                        </div>
                        <div className="h-[400px] rounded-sm overflow-hidden border border-slate-700">
                            <MapContainer center={[-3.5, -52.0]} zoom={6} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; OpenStreetMap'
                                />
                                <PolygonDrawer pontos={pontos} setPontos={setPontos} ativo={criando} />
                            </MapContainer>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCriando(false)}>Cancelar</Button>
                            <Button onClick={salvar} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-geofence">
                                <Save className="w-4 h-4 mr-2" /> Salvar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
